/**
 * Touch input producer: a virtual stick under the left thumb, drag-to-look on the right,
 * and a tap anywhere to interact with whatever was tapped.
 *
 * The hard part here is not drawing a stick, it is multi-touch correctness. Each stick
 * tracks its own `pointerId`; an implementation that follows a single active touch breaks
 * the instant a player moves and looks at once, which is every second of play. That is
 * also why this is hand-rolled rather than taken from a library.
 *
 * The movement stick spawns wherever the thumb lands rather than sitting in a fixed
 * corner, because thumb position varies with grip and hand size.
 */

import { TOUCH_LOOK_GAIN } from '../sensitivity';
import { resetInput, type InputState, type Vec2 } from '../inputState';

/** How far the thumb travels from the stick origin for full speed. */
export const STICK_RADIUS_PX = 60;

/**
 * How far a touch may wander and still count as a tap rather than a drag.
 *
 * Generous, because a thumb on glass is not a mouse: pressing hard rolls the contact
 * point by several pixels on its own. The cost of being generous is only that a very
 * short look-flick reads as a tap; the cost of being mean is taps that do nothing.
 */
export const TAP_SLOP_PX = 12;

/**
 * Screen point to normalized device coordinates: the form a camera can turn back into a
 * ray. Centre is the origin, edges are ±1, and the vertical axis flips because screen y
 * grows downward while clip space grows up.
 */
export function pointerToNdc(point: Vec2, viewport: { width: number; height: number }): Vec2 {
  if (viewport.width <= 0 || viewport.height <= 0) return { x: 0, y: 0 };

  return {
    x: (point.x / viewport.width) * 2 - 1,
    y: -((point.y / viewport.height) * 2 - 1),
  };
}

/**
 * Where the stick is pushed, as an analog vector no longer than 1.
 *
 * Screen y grows downward while forward is up, so the vertical axis is flipped here —
 * getting this wrong makes the game feel upside down rather than obviously broken.
 */
export function stickVector(origin: Vec2, current: Vec2, radius: number): Vec2 {
  if (radius <= 0) return { x: 0, y: 0 };

  const dx = current.x - origin.x;
  const dy = current.y - origin.y;
  const distance = Math.hypot(dx, dy);
  if (distance === 0) return { x: 0, y: 0 };

  const magnitude = Math.min(distance, radius) / radius;

  return {
    x: (dx / distance) * magnitude,
    y: -(dy / distance) * magnitude,
  };
}

/**
 * How far past the stick's ring the thumb goes to ask for a run: 1.5 ring radii, 90 px
 * against a 60 px ring. Past full speed, and far enough that it cannot be reached by
 * accident while steering (design D3).
 */
export const SPRINT_REACH = 1.5;

/**
 * How much of that push has to be *forward*: the vertical share of the displacement,
 * 0.7 being roughly a 45° cone around straight up.
 */
export const SPRINT_FORWARD_BIAS = 0.7;

/**
 * Whether the stick is being pushed hard enough, and forward enough, to be asking for a
 * run. Distance past `radius * SPRINT_REACH` alone is not enough: a thumb sweeping the
 * stick around a corner leaves the ring sideways constantly, and a sprint there would
 * speed up exactly the turns that need care.
 *
 * Independent of `stickVector`, which still clamps at 1 — inside the ring the stick stays
 * proportional, and the extra travel means speed rather than more of it.
 */
export function sprintFromStick(origin: Vec2, current: Vec2, radius: number): boolean {
  if (radius <= 0) return false;

  const dx = current.x - origin.x;
  // Screen y grows downward while forward is up.
  const forward = origin.y - current.y;
  const distance = Math.hypot(dx, forward);
  if (distance <= radius * SPRINT_REACH) return false;

  return forward / distance >= SPRINT_FORWARD_BIAS;
}

/** What the on-screen stick should draw, or null when no thumb is down. */
export type StickView = { origin: Vec2; current: Vec2 } | null;

type Options = {
  state: InputState;
  /** The element covering the play area. */
  surface: HTMLElement;
  /** Called when the movement stick appears, moves or disappears, for rendering it. */
  onStickChange?: (stick: StickView) => void;
};

type TouchLikeEvent = Event & {
  pointerId: number;
  clientX: number;
  clientY: number;
  pointerType?: string;
};

/**
 * Both producers stay attached at once — a tablet with a keyboard is a real device, and
 * asking the player to pick a mode is worse than supporting both. The mouse therefore has
 * to be filtered out here, or a desktop click would spawn a movement stick.
 */
function isFingerLike(event: TouchLikeEvent): boolean {
  return event.pointerType === undefined || event.pointerType === 'touch' || event.pointerType === 'pen';
}

/**
 * Touches that begin on a HUD control belong to that control. Without this, tapping a
 * button also spawns a movement stick underneath it.
 */
function startsOnHudControl(event: Event): boolean {
  const target = event.target;
  return target instanceof Element && target.closest('[data-hud]') !== null;
}

/**
 * A finger that is down and might still turn out to be a tap. Every finger-like touch on
 * the play surface gets one, whether or not it drives a stick, so that a tap is decided by
 * the gesture rather than by which half of the screen it landed on.
 */
type Press = { start: Vec2; moved: boolean };

export function attachTouch({ state, surface, onStickChange }: Options): () => void {
  let movePointerId: number | null = null;
  let lookPointerId: number | null = null;
  let stickOrigin: Vec2 = { x: 0, y: 0 };
  let lastLook: Vec2 = { x: 0, y: 0 };
  const presses = new Map<number, Press>();

  const isLeftHalf = (clientX: number) => clientX < window.innerWidth / 2;

  const clearStick = () => {
    movePointerId = null;
    state.move.x = 0;
    state.move.y = 0;
    state.sprint = false;
    onStickChange?.(null);
  };

  const onPointerDown = (event: Event) => {
    const touch = event as TouchLikeEvent;
    if (!isFingerLike(touch) || startsOnHudControl(event)) return;

    presses.set(touch.pointerId, {
      start: { x: touch.clientX, y: touch.clientY },
      moved: false,
    });

    if (isLeftHalf(touch.clientX)) {
      // A second thumb on the same half is ignored rather than moving the stick out from
      // under the thumb already using it.
      if (movePointerId !== null) return;
      movePointerId = touch.pointerId;
      stickOrigin = { x: touch.clientX, y: touch.clientY };
      onStickChange?.({ origin: stickOrigin, current: stickOrigin });
    } else {
      if (lookPointerId !== null) return;
      lookPointerId = touch.pointerId;
      lastLook = { x: touch.clientX, y: touch.clientY };
    }
  };

  const onPointerMove = (event: Event) => {
    const touch = event as TouchLikeEvent;

    // Once a finger has travelled, it is a drag for good — a look that swings out and
    // comes back to where it started is not a tap, however it ends.
    const press = presses.get(touch.pointerId);
    if (press && !press.moved) {
      const travel = Math.hypot(touch.clientX - press.start.x, touch.clientY - press.start.y);
      if (travel > TAP_SLOP_PX) press.moved = true;
    }

    if (touch.pointerId === movePointerId) {
      const current = { x: touch.clientX, y: touch.clientY };
      const move = stickVector(stickOrigin, current, STICK_RADIUS_PX);
      state.move.x = move.x;
      state.move.y = move.y;
      // Level, not edge: eased back inside the reach, the player is walking again.
      state.sprint = sprintFromStick(stickOrigin, current, STICK_RADIUS_PX);
      onStickChange?.({ origin: stickOrigin, current });
      return;
    }

    if (touch.pointerId === lookPointerId) {
      // Pixels scaled by the touch gain — see `TOUCH_LOOK_GAIN`. Sensitivity and
      // inversion are still applied when the delta is consumed.
      state.look.x += (touch.clientX - lastLook.x) * TOUCH_LOOK_GAIN;
      state.look.y += (touch.clientY - lastLook.y) * TOUCH_LOOK_GAIN;
      lastLook = { x: touch.clientX, y: touch.clientY };
    }
  };

  const onPointerEnd = (event: Event) => {
    const touch = event as TouchLikeEvent;

    const press = presses.get(touch.pointerId);
    presses.delete(touch.pointerId);
    // A cancelled touch is not a tap: the system took the finger away, the player did
    // not lift it. Where the finger *landed* is what the player aimed at, so the tap is
    // reported from the start point rather than from wherever it drifted to.
    if (press && !press.moved && event.type === 'pointerup') {
      state.tap = pointerToNdc(press.start, {
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    if (touch.pointerId === movePointerId) clearStick();
    else if (touch.pointerId === lookPointerId) lookPointerId = null;
  };

  surface.addEventListener('pointerdown', onPointerDown);
  // Move and end are listened for on the document: a thumb regularly slides outside the
  // element it started on, and a touch cancelled by the OS may not report on the surface.
  document.addEventListener('pointermove', onPointerMove);
  document.addEventListener('pointerup', onPointerEnd);
  document.addEventListener('pointercancel', onPointerEnd);

  return () => {
    surface.removeEventListener('pointerdown', onPointerDown);
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerEnd);
    document.removeEventListener('pointercancel', onPointerEnd);
    movePointerId = null;
    lookPointerId = null;
    presses.clear();
    resetInput(state);
    onStickChange?.(null);
  };
}
